import type { VideoType } from "@sfc/database";

/**
 * The one description of what each kind of video IS and where it belongs.
 *
 * Before this table, "which kinds are funder-facing" was a literal list of
 * type names retyped in about eight places (the meetings query's allowlist and
 * its separate playability check, the track rail's meeting-vs-clip counts, the
 * queue's auto-advance gate, the recommender/organization clip rails, the
 * speaker/organization tagging gates, and the summary triggers). Every kind
 * added after those lists were written was therefore invisible on the funder
 * website while showing up correctly on the iPhone — not a decision, just the
 * difference between a whitelist and a "group by whatever you find" list.
 *
 * So: add a kind here, and every webapp surface picks it up. If you find
 * yourself writing `type === "EXCERPT" || type === "..."` anywhere, the answer
 * you want is a field on this table instead.
 *
 * Scope: this covers the WEBAPP (desktop and iPad — one codebase). The iPhone
 * shell still keeps its own label/order copy; see `displayOrder` below.
 */

/** A funder-facing place videos are listed. */
export type VideoSurface =
  /** The per-track video strip (Review → a track). */
  | "trackStrip"
  /** The round-level strip above the per-track panels (Review → Overview). */
  | "roundStrip"
  /** A single recommender's clips strip (Review → a recommender). */
  | "recommenderRail"
  /** A single organization's clips strip (Review → an organization). */
  | "organizationRail";

/**
 * The SHAPE of a kind's generated summary. Independent of whether it gets
 * chapters — see `hasChapters`.
 *
 * These were one `treatment` field deciding both, which is the same
 * one-flag-two-jobs mistake that produced several bugs in the previous change
 * (a shared `taggable` boolean covering two tag kinds, and a per-unit predicate
 * standing in for a per-track one). Chapters and summary shape are genuinely
 * independent axes, so they are two fields.
 *
 * - `clip` — one short summary (title / one-line description / short summary
 *   plus per-person lines). What the recommender and organization rails render
 *   on a card, so any kind shown there must produce it.
 * - `meeting` — the long-form pipeline: condensed transcript plus the
 *   proposal/recommender summaries.
 * - `none` — never summarized.
 */
export type VideoSummaryShape = "clip" | "meeting" | "none";

/**
 * Where a kind sits in the To Do rail — the funder's ordered walkthrough of a
 * round (Review → Decide), rendered by the rail drawer.
 *
 * A kind is on the rail because it is something the funder is EXPECTED to
 * work through, which is not the same question as any other field here — note
 * that `order` deliberately disagrees with `displayOrder`. Display order is
 * widest-synthesis-first within one strip (a recommender overview is a
 * short synthesis, so it sorts second); rail order is the sequence a funder
 * should move through a round (recommenders come after the tracks, because
 * you watch the tracks to understand what the recommenders are discussing).
 * Deriving one from the other would silently reorder the walkthrough.
 *
 * `optional` marks a kind that nests UNDER the required step for its track
 * rather than being a step of its own, and is excluded from that step's
 * progress — skipping every meeting cut must never leave the rail looking
 * unfinished.
 */
export interface VideoKindTodoRailSpec {
  /** Position among the rail's video steps. Shared by a required kind and the optional kinds nested under it. */
  order: number;
  /** Nested under its step and excluded from that step's progress. */
  optional: boolean;
}

export interface VideoKindSpec {
  /** Singular display name — one card, an admin picker row. */
  label: string;
  /** Plural heading for a group of them ("Excerpts" over a list of three). */
  groupLabel: string;
  /**
   * Canonical position wherever the WEBAPP groups videos by kind — every funder
   * strip and rail on desktop and iPad, which are one codebase. Ordered
   * widest-synthesis-first and rawest-last, so a funder reaches every short
   * version before the two-hour recording.
   *
   * NOT yet canonical for the iPhone shell: `client/iphone/lib/videoTypeLabels.ts`
   * still keeps its own labels and its own order (Round overview, Onboarding,
   * Meeting cut, Track overview, Recommender overview, Excerpt, Unabridged),
   * which differs from this one. That duplication is known and deliberately
   * out of scope here — deleting it is the remaining half of making the two
   * shells agree. Don't assume the shells match until that lands.
   */
  displayOrder: number;
  /** Surfaces this kind is listed on. Empty = not funder-facing. */
  surfaces: readonly VideoSurface[];
  /**
   * True when the unit only counts as funder-facing if it carries transcript
   * text. Only the unabridged recordings do: a raw recording with no
   * transcript is an unedited file nobody curated, while every curated cut is
   * watchable on its own.
   */
  requiresTranscript: boolean;
  /**
   * Whether this kind's transcript is an INPUT to its track's combined summary.
   *
   * True only for the unedited source recordings. Everything else is a
   * derivative or a synthesis of material already counted:
   * - A meeting cut is a shortened version of a recording sitting beside it, so
   *   feeding both fed the same conversation in twice, which reads to the model
   *   as emphasis the discussion never had.
   * - A track overview IS the track's synthesis, so feeding it into that same
   *   track's combined summary means summarising your own summary.
   * - Clip kinds carry no per-meeting summary to roll up.
   *
   * Deliberately an explicit field, not derived from another attribute. It
   * encodes a product decision about summary quality, and every attribute it
   * currently correlates with (requiresTranscript, summaryShape) would only
   * correlate — the previous change shipped several bugs by deriving one
   * question from another that merely happened to agree.
   */
  feedsTrackSummary: boolean;
  /** Shape of the generated summary. */
  summaryShape: VideoSummaryShape;
  /**
   * Whether the kind gets generated chapters. Every kind except excerpts does:
   * an excerpt is a single short stretch with nothing to navigate, while a cut,
   * an overview, or a full recording is long enough that chapters are how a
   * funder finds their way around it.
   */
  hasChapters: boolean;
  /**
   * Whether excerpts can be cut FROM this kind — i.e. whether an EXCERPT row's
   * `sourceVideoId` may point at it.
   *
   * A product rule, not derivable from the other fields: the curated cuts and
   * syntheses (round overview, track overviews, meeting cuts) are what editors
   * clip excerpts out of. Unabridged recordings are deliberately NOT sources —
   * nobody excerpts the raw two-hour file directly; they excerpt the cut made
   * from it. Excerpts of excerpts don't exist either (an excerpt IS the atom).
   */
  sourcesExcerpts: boolean;
  /**
   * This kind's place in the To Do rail's ordered walkthrough, or null when the
   * kind is not something a funder works through in sequence (a hand-cut
   * excerpt, the unabridged recording behind a cut, the onboarding tutorial).
   */
  todoRail: VideoKindTodoRailSpec | null;
}

/**
 * Typed as `Record<VideoType, …>` on purpose: adding a member to the Prisma
 * `VideoType` enum stops this file compiling until the new kind has been
 * described, which is what makes the rest of the app safe to drive off it.
 */
export const videoKinds: Record<VideoType, VideoKindSpec> = {
  EXPLAINER: {
    label: "Explainer",
    groupLabel: "Explainers",
    displayOrder: 8,
    surfaces: [],
    requiresTranscript: false,
    feedsTrackSummary: false,
    summaryShape: "none",
    hasChapters: false,
    sourcesExcerpts: false,
    todoRail: null,
  },
  ROUND_OVERVIEW: {
    label: "Round overview",
    groupLabel: "Round overview",
    displayOrder: 1,
    // Round-scoped, not track-scoped — it has no track to file under, which is
    // why it was invisible on the website even though the strip is the obvious
    // home for it.
    surfaces: ["roundStrip"],
    requiresTranscript: false,
    feedsTrackSummary: false,
    summaryShape: "meeting",
    hasChapters: true,
    sourcesExcerpts: true,
    // Step 1: the round's own framing video, before any track detail.
    todoRail: { order: 1, optional: false },
  },
  RECOMMENDER_OVERVIEW: {
    label: "Recommender overview",
    groupLabel: "Recommender overviews",
    displayOrder: 2,
    // Leads a recommender's clips strip, above their excerpts — its
    // displayOrder is what puts it first.
    surfaces: ["recommenderRail"],
    requiresTranscript: false,
    feedsTrackSummary: false,
    summaryShape: "clip",
    hasChapters: true,
    sourcesExcerpts: false,
    // Step 3: one required step per recommender, after the tracks.
    todoRail: { order: 3, optional: false },
  },
  TRACK_OVERVIEW: {
    label: "Track overview",
    groupLabel: "Track overviews",
    displayOrder: 3,
    surfaces: ["trackStrip"],
    requiresTranscript: false,
    // The long-form curated cut for a whole track, so it keeps the meeting
    // pipeline (chapters + summaries) it gets today rather than being demoted
    // to a single short summary.
    feedsTrackSummary: false,
    summaryShape: "meeting",
    hasChapters: true,
    sourcesExcerpts: true,
    // Step 2: one required step per track.
    todoRail: { order: 2, optional: false },
  },
  MEETING_CUT: {
    label: "Meeting cut",
    groupLabel: "Meeting cuts",
    displayOrder: 4,
    surfaces: ["trackStrip"],
    requiresTranscript: false,
    feedsTrackSummary: false,
    summaryShape: "meeting",
    hasChapters: true,
    sourcesExcerpts: true,
    // Optional extra nested under its track's step ("watch the meeting
    // highlights") — never required, never counted toward that step.
    todoRail: { order: 2, optional: true },
  },
  EXCERPT: {
    label: "Excerpt",
    groupLabel: "Excerpts",
    displayOrder: 5,
    // The one kind that appears everywhere: a hand-cut clip shows up in its
    // track's strip and on the rail of every person and organization it is
    // tagged with.
    surfaces: ["trackStrip", "recommenderRail", "organizationRail"],
    requiresTranscript: false,
    feedsTrackSummary: false,
    summaryShape: "clip",
    hasChapters: false,
    sourcesExcerpts: false,
    // A hand-cut clip surfaced in context on a rail, not a sequenced step.
    todoRail: null,
  },
  RAW_RECORDING: {
    label: "Unabridged recording",
    groupLabel: "Unabridged recordings",
    displayOrder: 6,
    surfaces: ["trackStrip"],
    requiresTranscript: true,
    feedsTrackSummary: true,
    summaryShape: "meeting",
    hasChapters: true,
    sourcesExcerpts: false,
    // The unabridged source behind a cut — the rail sends funders to the cut.
    todoRail: null,
  },
  ONBOARDING: {
    label: "Onboarding",
    groupLabel: "Onboarding",
    displayOrder: 7,
    // Not round-scoped and not part of any round's lists — the onboarding
    // tutorial has its own page, reached by its own route.
    surfaces: [],
    requiresTranscript: false,
    feedsTrackSummary: false,
    summaryShape: "none",
    hasChapters: false,
    sourcesExcerpts: false,
    // Not part of any round's walkthrough; it has its own route.
    todoRail: null,
  },
};

/**
 * Old enum labels → their current names.
 *
 * A rename has to sweep every store holding the value, not just the database.
 * These three names were renamed in the DB by migration, but they also live in
 * each device's PERSISTED CATALOG (`mobileVideos.v*`), which is written by
 * whatever build was installed at the time. An installed app that upgrades
 * before it can refresh the catalog still holds the old labels, and every
 * predicate here would treat them as unrecognised kinds — a saved recommender
 * overview would stop being rail-only and its queue link would point at a track
 * page it can't resolve from.
 *
 * Consumed when hydrating the persisted store (see `coerceMobileVideoType`).
 * Keep entries forever: they cost nothing and removing one silently breaks the
 * caches that still hold it.
 */
export const LEGACY_VIDEO_KIND_ALIASES: Record<string, VideoType> = {
  TRACK_MEETING_HIGHLIGHTS: "MEETING_CUT",
  TRACK_DEEP_DIVE: "TRACK_OVERVIEW",
  RECOMMENDER_GROUP_ROLLUP: "RECOMMENDER_OVERVIEW",
};

/** The current name for a kind label, mapping a renamed one forward. */
export function canonicalVideoKind(type: string): string {
  return LEGACY_VIDEO_KIND_ALIASES[type] ?? type;
}

/** Every kind in canonical display order. */
export const videoKindsInDisplayOrder: VideoType[] = (
  Object.keys(videoKinds) as VideoType[]
).sort((a, b) => videoKinds[a].displayOrder - videoKinds[b].displayOrder);

/**
 * Whether a kind can be tagged with the PEOPLE it features.
 *
 * Derived from the surface those tags feed, not stored: people tags exist to
 * place a video on a recommender's rail, so the kinds that accept them are
 * exactly the kinds listed there. A stored boolean here was a duplicate of
 * `surfaces` that could disagree with it — and did: one flag covering both tag
 * kinds showed the ORGANIZATION picker for recommender rollups, whose panel
 * promises "appears on each tagged organization's page" while the organization
 * rail never returns them.
 */
export function acceptsPeopleTags(type: string): boolean {
  return isVideoKindOnSurface(type as VideoType, "recommenderRail");
}

/**
 * Whether a kind can be tagged with the ORGANIZATIONS it discusses. Derived
 * from the organization rail for the same reason as {@link acceptsPeopleTags}.
 */
export function acceptsOrganizationTags(type: string): boolean {
  return isVideoKindOnSurface(type as VideoType, "organizationRail");
}

/** Kinds that accept tags of either sort, in canonical display order. */
export const taggableVideoKinds: VideoType[] = videoKindsInDisplayOrder.filter(
  (type) => acceptsPeopleTags(type) || acceptsOrganizationTags(type),
);

/**
 * Kinds that can produce a linkable (recommender, organization) pair — the
 * Watch pill's index. Needs BOTH tag kinds, so it is narrower than
 * `taggableVideoKinds`: a kind carrying only people tags can never name an
 * organization to pair with.
 */
export const recommenderOrgLinkableVideoKinds: VideoType[] =
  videoKindsInDisplayOrder.filter(
    (type) => acceptsPeopleTags(type) && acceptsOrganizationTags(type),
  );

/**
 * Kinds on the clip pipeline — one short clip-style summary, no chapters, and
 * excluded from a track's combined summary (they're hand-cut duplicates of
 * meeting content, not extra meetings to roll up).
 *
 * This is the set to reach for instead of writing `type === "EXCERPT"`. Every
 * gate that decides "clip summary or meeting summaries?" must read it, on BOTH
 * sides: a read path and its paired trigger that disagree produce rows the
 * admin UI advertises but no click can run, or paid LLM passes on content that
 * should never have been eligible.
 */
export const clipTreatmentVideoKinds: VideoType[] =
  videoKindsInDisplayOrder.filter(
    (type) => videoKinds[type].summaryShape === "clip",
  );

/**
 * Whether a kind renders ONLY inside a person's or organization's clips rail —
 * a recommender rollup today — and so has no strip URL at all.
 *
 * Which rail item shows it comes from the video's tags, which the stored watch
 * queue doesn't carry, so no URL is derivable from (videoId, track, kind).
 * Callers must treat this as "no destination" rather than falling through to a
 * track URL that resolves to nothing.
 *
 * Note the deliberate asymmetry with the other predicates here: an UNRECOGNISED
 * kind is not rail-only. Unknown almost always means "written by a newer build",
 * and the common case is track-scoped, so an unknown kind should degrade to the
 * ordinary track link rather than being routed somewhere arbitrary. A kind on no
 * surface at all (onboarding) likewise isn't rail-only — it's simply not in any
 * round's lists.
 */
export function isRailOnlyVideoKind(type: string): boolean {
  const kind = videoKinds[type as VideoType];
  if (kind == null || kind.surfaces.length === 0) return false;
  return !(
    kind.surfaces.includes("trackStrip") || kind.surfaces.includes("roundStrip")
  );
}

/**
 * Kinds whose transcripts count toward a TRACK's combined summary: on the
 * meeting pipeline AND scoped to a track.
 *
 * Deliberately a positive `in` set rather than an exclusion list. Both halves
 * matter, and an exclusion list got each one wrong in turn:
 * - Clip-pipeline kinds carry no per-meeting summary to roll up.
 * - Round-level kinds are round-wide synthesis. They carry a track (every
 *   upload does), so an exclusion list swept a round overview into one
 *   arbitrary track's rollup — blocking that track's "Summarize all" until the
 *   overview was summarized, then folding whole-round content into one track.
 *   Onboarding, on no surface at all, slipped through the same way.
 *
 * With an `in` set a newly added kind is excluded until someone opts it in,
 * which is the safe default: wrongly including a transcript corrupts a track's
 * synthesis, while wrongly excluding one is a visible "not run".
 */
export const trackRollupVideoKinds: VideoType[] =
  videoKindsInDisplayOrder.filter((type) => videoKinds[type].feedsTrackSummary);

/**
 * Kinds a track's "generate all chapters" action covers: chaptered kinds that
 * live on a track's strip.
 *
 * NOT the same as `trackRollupVideoKinds`, and the difference is the point — a
 * meeting cut and a track overview both get chapters but do not feed the track's
 * combined summary. Using the rollup set here would silently stop chaptering
 * them.
 */
export const trackChapterableVideoKinds: VideoType[] =
  videoKindsInDisplayOrder.filter(
    (type) =>
      videoKinds[type].hasChapters &&
      videoKinds[type].surfaces.includes("trackStrip"),
  );

/** Predicate form of {@link trackChapterableVideoKinds}. */
export function isTrackChapterableVideoKind(type: string): boolean {
  const kind = videoKinds[type as VideoType];
  if (kind == null) return false;
  return kind.hasChapters && kind.surfaces.includes("trackStrip");
}

/**
 * Predicate form of {@link trackRollupVideoKinds}, for client-side filters.
 *
 * **Choosing between this and `isClipTreatmentKind` is the distinction to get
 * right**, because the two look interchangeable and are not:
 *
 * - `isClipTreatmentKind` answers a PER-UNIT question — "can this one video get
 *   a meeting summary / chapters?" Use it for row-level gates, the single
 *   triggers, and the video detail page. A round overview individually can.
 * - This answers a PER-TRACK AGGREGATE question — "which videos make up this
 *   track's set?" Use it for the summarized badge, Summarize All, the combined
 *   summary, and bulk-per-track actions. A round overview does NOT belong to any
 *   one track's set, even though it carries a track.
 *
 * Using the per-unit predicate for an aggregate gate is what left the Combined
 * Summary button disabled until an unrelated video was summarized: the client
 * counted a round overview toward the track while the server excluded it.
 */
export function isTrackRollupVideoKind(type: string): boolean {
  return videoKinds[type as VideoType]?.feedsTrackSummary ?? false;
}

/**
 * Whether a kind lives on the round-level Overview rail rather than a track's.
 *
 * **This is the only correct way to ask that question.** `finalizeVideoUpload`
 * requires a track on every new video, so a round overview carries one — asking
 * "does it have a track?" gets the wrong answer and points the viewer at a track
 * page where `getMeetings` deliberately never returns it, so the `?video=`
 * target silently fails to resolve. Every path that turns a video into a
 * location — deep links, queue navigation, passage/turn links — must key on
 * this, never on the track column.
 */
export function isRoundLevelVideoKind(type: string): boolean {
  return isVideoKindOnSurface(type as VideoType, "roundStrip");
}

/**
 * Whether a kind is STANDALONE — on no funder surface at all, reached by its
 * own route rather than any round's lists (onboarding today).
 *
 * Not the same as "unknown": an unrecognised kind returns false here (and true
 * from {@link isTrackScopedVideoKind}), so a kind written by a newer build
 * degrades to ordinary track grouping instead of vanishing into a section the
 * old build doesn't render.
 */
export function isStandaloneVideoKind(type: string): boolean {
  return videoKinds[type as VideoType]?.surfaces.length === 0;
}

/**
 * Whether a kind is managed WITHIN a track: filed under its track's section on
 * the admin Videos page, required to name a track at upload, and a member of
 * its track's sortOrder group — the unit the reorder/move routes validate
 * against (`transcriptGroupWhere`).
 *
 * Everything that is neither round-level, rail-only, nor standalone. Rail-only
 * kinds (a recommender overview today) used to file under the track they came
 * from, but a recommender's overview spans that person's whole involvement in
 * the round — filing it under one track was as arbitrary as it was for the
 * round overview, so they get their own kind-scoped admin section instead and
 * upload without a track.
 *
 * An UNRECOGNISED kind is track-scoped: unknown almost always means "written
 * by a newer build", the common case is track-scoped, and degrading to
 * ordinary track grouping keeps the row visible in a section this build
 * renders.
 *
 * The client sections and the server's group where-clause must share this
 * predicate: a kind one side files under a track and the other doesn't makes
 * every drag-reorder in that track submit a list the server rejects as
 * incomplete.
 */
export function isTrackScopedVideoKind(type: string): boolean {
  return (
    !isRoundLevelVideoKind(type) &&
    !isStandaloneVideoKind(type) &&
    !isRailOnlyVideoKind(type)
  );
}

/** Kinds in a track's sortOrder group, for server `in` filters. */
export const trackScopedVideoKinds: VideoType[] =
  videoKindsInDisplayOrder.filter((type) => isTrackScopedVideoKind(type));

/**
 * Whether a kind is on the clip pipeline. Tolerates an unrecognised value
 * (returns false) for the same reason as `isVideoKindOnSurface` — persisted and
 * cached kind strings can outlive a build.
 */
export function isClipTreatmentKind(type: string): boolean {
  return videoKinds[type as VideoType]?.summaryShape === "clip";
}

/**
 * Kinds that get generated chapters, in canonical display order. Everything
 * except excerpts.
 *
 * Chapters eligibility is its OWN axis — do not reach for the summary-shape
 * predicates here. A recommender overview takes the `clip` summary shape (so its
 * rail card has something to render) but is long enough to want chapters, so the
 * two answers differ for it. Using the summary shape as a proxy would silently
 * deny it chapters.
 */
export const chapteredVideoKinds: VideoType[] = videoKindsInDisplayOrder.filter(
  (type) => videoKinds[type].hasChapters,
);

/** Whether a kind gets generated chapters. Tolerates an unrecognised value. */
export function hasChaptersVideoKind(type: string): boolean {
  return videoKinds[type as VideoType]?.hasChapters ?? false;
}

/** Kinds listed on a surface, in canonical display order. */
export function videoKindsOnSurface(surface: VideoSurface): VideoType[] {
  return videoKindsInDisplayOrder.filter((type) =>
    videoKinds[type].surfaces.includes(surface),
  );
}

/**
 * Whether a kind is listed on a surface.
 *
 * Tolerates an unrecognised value rather than throwing: the persisted watch
 * queue and offline caches hold kind strings written by an older build, so a
 * kind that has since been renamed or removed can reach this at runtime. An
 * unknown kind is on no surface.
 */
export function isVideoKindOnSurface(
  type: VideoType,
  surface: VideoSurface,
): boolean {
  return videoKinds[type]?.surfaces.includes(surface) ?? false;
}

/**
 * Comparator for grouping/sorting by kind. Use with a STABLE sort so the
 * caller's within-kind ordering (a track's manual drag order, then upload
 * time) survives.
 *
 * An unrecognised kind sorts last rather than throwing — same reasoning as
 * `isVideoKindOnSurface`, and it keeps a stale queue entry at the end of a list
 * instead of crashing the render that contains it.
 */
export function compareVideoKindOrder(a: VideoType, b: VideoType): number {
  const rank = (type: VideoType) =>
    videoKinds[type]?.displayOrder ?? Number.MAX_SAFE_INTEGER;
  return rank(a) - rank(b);
}

/**
 * Whether a kind is a hand-cut excerpt of another video — the one kind that
 * carries a `sourceVideoId` lineage link. Centralised so "is this an excerpt?"
 * is asked one way; tolerates unrecognised values like the other predicates.
 */
export function isExcerptVideoKind(type: string): boolean {
  return (type as VideoType) === "EXCERPT";
}

/**
 * Kinds an excerpt's `sourceVideoId` may point at, in canonical display
 * order — see the `sourcesExcerpts` field for why unabridged recordings and
 * excerpts themselves are not sources. Upload validation, the set-source
 * route, and the admin source pickers must all read this same set.
 */
export const excerptSourceVideoKinds: VideoType[] =
  videoKindsInDisplayOrder.filter((type) => videoKinds[type].sourcesExcerpts);

/** Predicate form of {@link excerptSourceVideoKinds}. Tolerates unknown values. */
export function isExcerptSourceVideoKind(type: string): boolean {
  return videoKinds[type as VideoType]?.sourcesExcerpts ?? false;
}

/**
 * Whether a kind reaches a recommender's/organization's rail ONLY as the
 * lineage LEAD of the excerpts cut from it — a thumbnail naming where those
 * clips came from, not something that rail plays.
 *
 * Derived, not stored: a lead is a kind excerpts are cut FROM
 * (`sourcesExcerpts`) that is not itself listed on either rail. A meeting cut
 * today. Those kinds already have a home that plays them in full — their own
 * track strip, with chapters and the whole transcript — which is why the rails
 * deliberately never ship their transcripts (see `loadFunderExcerptVideos`),
 * and why the rails treat them as navigation.
 *
 * A rail-native kind is NOT a lead even when it heads the list: a recommender
 * overview's only funder-facing home IS that rail, so making it unplayable
 * there would leave no surface that can watch it at all.
 *
 * Tolerates an unrecognised value (not a lead) for the same reason as the other
 * predicates here: cached rail payloads hold kind strings written by an older
 * build, and before lineage existed every row on these rails was an excerpt.
 */
export function isRailGroupLeadVideoKind(type: string): boolean {
  const kind = videoKinds[type as VideoType];
  if (kind == null) return false;
  return (
    kind.sourcesExcerpts &&
    !kind.surfaces.includes("recommenderRail") &&
    !kind.surfaces.includes("organizationRail")
  );
}

/** Every summarized video can link names in its raw transcript, independently
 * of the separate linking pass on generated summary prose. */
export const entityLinkableVideoKinds: VideoType[] =
  videoKindsInDisplayOrder.filter((type) => isEntityLinkableVideoKind(type));

export function isEntityLinkableVideoKind(type: string): boolean {
  const kind = videoKinds[type as VideoType];
  return kind != null && kind.summaryShape !== "none";
}

/**
 * Kinds a track's "generate all entity links" action covers: entity-linkable
 * kinds that live on a track's strip. The track-scoped mirror of
 * {@link trackChapterableVideoKinds}, and distinct from
 * {@link entityLinkableVideoKinds} for the same reason that set is distinct
 * from `chapteredVideoKinds`: a round overview can be linked individually but
 * belongs to no one track's set.
 */
export const trackEntityLinkableVideoKinds: VideoType[] =
  videoKindsInDisplayOrder.filter(
    (type) =>
      isEntityLinkableVideoKind(type) &&
      videoKinds[type].surfaces.includes("trackStrip"),
  );

/** Predicate form of {@link trackEntityLinkableVideoKinds}. */
export function isTrackEntityLinkableVideoKind(type: string): boolean {
  const kind = videoKinds[type as VideoType];
  if (kind == null) return false;
  return (
    isEntityLinkableVideoKind(type) && kind.surfaces.includes("trackStrip")
  );
}

/**
 * Rail ordering rank for a clip row's LINEAGE: 0 for non-excerpt kinds (their
 * own kind order governs), 1 for excerpts cut from a meeting cut (the rails'
 * primary material), 2 for every other excerpt (overview-sourced or
 * unlinked). Shared by the server rail loaders and the client strip so the
 * displayed order, the early-media lead pick, and cached-payload reordering
 * can never disagree.
 */
export function excerptLineageRank(
  type: string,
  sourceVideoType: string | null | undefined,
): number {
  if (!isExcerptVideoKind(type)) return 0;
  return (sourceVideoType as VideoType) === "MEETING_CUT" ? 1 : 2;
}

/**
 * This kind's To Do rail placement, or null when it isn't a rail step.
 *
 * Tolerates an unrecognised value for the same reason as the other predicates
 * here: cached catalogs hold kind strings written by an older build, and a kind
 * this build doesn't know can't be placed in the walkthrough — leaving it off
 * the rail is the safe answer, since the rail is a recommendation rather than
 * a gate and nothing becomes unreachable.
 */
export function videoKindTodoRail(type: string): VideoKindTodoRailSpec | null {
  return videoKinds[type as VideoType]?.todoRail ?? null;
}

/** Singular display name, falling back to the raw value for an unknown kind. */
export function videoKindLabel(type: string): string {
  return videoKinds[type as VideoType]?.label ?? type;
}

/** Plural group heading, falling back to the raw value for an unknown kind. */
export function videoKindGroupLabel(type: string): string {
  return videoKinds[type as VideoType]?.groupLabel ?? type;
}
